import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Icon } from '@/common/components/icon'
import { fetchApi } from '@/common/lib/fetchApi'
import { useAdminEmployees } from '@/common/hooks/useAdminEmployees'
import { useImpersonation } from '@/common/hooks/useImpersonation'
import * as styles from './ImpersonationPicker.css'

export function ImpersonationPicker() {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const { data: employees, isLoading } = useAdminEmployees(search || undefined)
  const { startImpersonating, isImpersonating } = useImpersonation()

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setIsOpen(false)
      setSearch('')
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus()
    }
  }, [isOpen])

  const handleSelect = async (employeeId: number, employeeName: string) => {
    const { roles } = await fetchApi<{ roles: string[] }>(
      `/api/org/employees/${employeeId}/roles`,
    )
    startImpersonating(employeeId, employeeName, roles)
    setIsOpen(false)
    setSearch('')
    navigate({ to: '/' })
  }

  if (isImpersonating) return null

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.triggerButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Icon name="person_search" size={18} />
        Impersonate
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <input
            ref={searchRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search employees..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.list} role="listbox">
            {isLoading && <div className={styles.emptyState}>Loading...</div>}
            {!isLoading && employees?.length === 0 && (
              <div className={styles.emptyState}>No employees found</div>
            )}
            {employees?.map((emp) => (
              <div
                key={emp.id}
                className={styles.item}
                role="option"
                aria-selected={false}
                onClick={() =>
                  handleSelect(emp.id, `${emp.first_name} ${emp.last_name}`)
                }
              >
                <span className={styles.itemName}>
                  {emp.first_name} {emp.last_name}
                </span>
                <span className={styles.itemMeta}>
                  {[emp.job_title, emp.department].filter(Boolean).join(' · ') || emp.email}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
